import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Dashboard,
  Storefront,
  RestaurantMenu,
  ReceiptLong,
  TableRestaurant,
  QrCode,
  Insights,
  PeopleAlt,
  CreditCard,
  Palette,
  OpenInNew,
  HelpOutline,
  Kitchen,
  PointOfSale,
  BellIcon,
} from '../ui/Icons';
import { useAuth } from '../../context/AuthContext';

export const SIDEBAR_WIDTH = 264;

/**
 * Dark-green left rail. Role-aware nav, plan card + restaurant switcher
 * at the bottom (for managers), and a small support card.
 *
 * Layout modes:
 *   md+    →  permanent fixed rail
 *   <md    →  slide-over (temporary Drawer) toggled by open/onClose
 */
const Sidebar = ({ open, onClose, variant }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const location = useLocation();
  const { user, restaurants, isAdmin } = useAuth();

  const navItems = isAdmin?.()
    ? [
        { id: 'overview',     to: '/admin/dashboard',     icon: <Dashboard />,        label: t('sidebar.overview',      'Overview') },
        { id: 'restaurants',  to: '/admin',               icon: <Storefront />,       label: t('sidebar.restaurants',   'Restaurants') },
        { id: 'orders',       to: '/admin/orders',        icon: <ReceiptLong />,      label: t('sidebar.orders',        'Orders') },
        { id: 'pos',          to: '/pos',                 icon: <PointOfSale />,      label: t('sidebar.pos',           'Point of Sale') },
        { id: 'qr-codes',     to: '/admin/qr-codes',      icon: <QrCode />,           label: t('sidebar.qrCodes',       'QR Codes') },
        { id: 'subscriptions',to: '/admin/subscriptions', icon: <CreditCard />,       label: t('sidebar.subscriptions', 'Subscriptions') },
        { id: 'users',        to: '/admin/users',         icon: <PeopleAlt />,        label: t('sidebar.users',         'Users & Roles') },
        { id: 'analytics',    to: '/admin/analytics',     icon: <Insights />,         label: t('sidebar.analytics',     'Analytics & Reports') },
      ]
    : [
        { id: 'dashboard',      to: '/manager/dashboard',         icon: <Dashboard />,       label: t('sidebar.dashboard',      'Dashboard') },
        { id: 'pos',            to: '/pos',                       icon: <PointOfSale />,     label: t('sidebar.pos',            'Point of Sale') },
        { id: 'orders',         to: '/admin/orders',              icon: <ReceiptLong />,     label: t('sidebar.orders',         'Orders') },
        { id: 'menu',           to: '/admin/menu-v2',             icon: <RestaurantMenu />,  label: t('sidebar.menu',           'Menu & Items') },
        { id: 'tables',         to: '/admin/qr-codes',            icon: <TableRestaurant />, label: t('sidebar.tables',         'Tables & QR Codes') },
        { id: 'restaurantInfo', to: '/manager/restaurant-info',   icon: <Storefront />,      label: t('sidebar.restaurantInfo', 'Restaurant Info') },
        { id: 'serviceRequests',to: '/manager/service-requests',  icon: <BellIcon />,        label: t('sidebar.serviceRequests','Service Requests') },
        { id: 'subscription',   to: '/subscription',              icon: <CreditCard />,      label: t('sidebar.subscription',   'Subscription') },
      ];

  // For manager dynamic links — use the first owned restaurant
  const primaryRestaurantId = restaurants?.[0]?.id;
  if (!isAdmin?.() && primaryRestaurantId) {
    navItems.push(
      { id: 'kitchen',   to: `/kitchen/${primaryRestaurantId}`,                       icon: <Kitchen />,  label: t('sidebar.kitchen',   'Kitchen Display') },
      { id: 'analytics', to: `/manager/restaurant/${primaryRestaurantId}/analytics`, icon: <Insights />, label: t('sidebar.analytics', 'Analytics & Reports') },
      { id: 'theme',     to: `/manager/restaurant/${primaryRestaurantId}/theme`,     icon: <Palette />,  label: t('sidebar.theme',     'Theme Settings') },
    );
  }

  const isActive = (to) => {
    if (!to) return false;
    if (to === '/admin') {
      // Avoid matching every /admin/* under the Restaurants/Menu item
      return location.pathname === '/admin';
    }
    return location.pathname === to || location.pathname.startsWith(`${to}/`);
  };

  const primaryRestaurant = restaurants?.[0];
  const customerMenuUrl = primaryRestaurant
    ? `/menu/${primaryRestaurant.slug || primaryRestaurant.id}`
    : null;

  const railContent = (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.mesoon?.sidebar?.bg || '#0F2D24',
        color: theme.palette.mesoon?.sidebar?.text || '#E2F1EA',
      }}
    >
      {/* Brand */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 3 }}>
        <Box
          component="img"
          src="/mesoon-logo.svg"
          alt=""
          sx={{ width: 36, height: 36, flexShrink: 0 }}
        />
        <Typography
          sx={{
            fontFamily: '"Plus Jakarta Sans", "IBM Plex Sans Arabic", Inter, sans-serif',
            fontSize: 22,
            fontWeight: 700,
            color: '#FFFFFF',
            letterSpacing: '-0.02em',
          }}
        >
          {t('brand.name', 'Mesoon')}
        </Typography>
        {/* Close button on mobile only */}
        {variant === 'temporary' && (
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              marginInlineStart: 'auto',
              color: theme.palette.mesoon?.sidebar?.textMuted || '#9DBDB1',
            }}
            aria-label={t('topbar.closeNav', 'Close navigation')}
          >
            <CloseIcon size={20} />
          </IconButton>
        )}
      </Box>

      {/* Nav */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5 }}>
        <List sx={{ py: 0 }}>
          {navItems.map((item) => {
            const active = isActive(item.to);
            return (
              <ListItemButton
                key={item.id}
                component={RouterLink}
                to={item.to}
                onClick={variant === 'temporary' ? onClose : undefined}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  py: 1.25,
                  px: 1.75,
                  color: active ? '#FFFFFF' : theme.palette.mesoon?.sidebar?.text || '#E2F1EA',
                  backgroundColor: active
                    ? theme.palette.mesoon?.sidebar?.bgActive || '#0F5B45'
                    : 'transparent',
                  '&:hover': {
                    backgroundColor: active
                      ? theme.palette.mesoon?.sidebar?.bgActive || '#0F5B45'
                      : theme.palette.mesoon?.sidebar?.bgHover || 'rgba(255,255,255,0.06)',
                  },
                  '& .MuiListItemIcon-root': {
                    minWidth: 36,
                    color: 'inherit',
                  },
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: active ? 600 : 500,
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Box>

      {/* Footer cards */}
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Restaurant switcher (managers only) */}
        {!isAdmin?.() && primaryRestaurant && (
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: 'rgba(255,255,255,0.04)',
              border: '1px solid',
              borderColor: theme.palette.mesoon?.sidebar?.border || 'rgba(255,255,255,0.08)',
            }}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#FFFFFF', mb: 0.5 }}>
              {primaryRestaurant.name}
            </Typography>
            {primaryRestaurant.location && (
              <Typography sx={{ fontSize: 12, color: theme.palette.mesoon?.sidebar?.textMuted || '#9DBDB1', mb: 1.5 }}>
                {primaryRestaurant.location}
              </Typography>
            )}
            {customerMenuUrl && (
              <ListItemButton
                component="a"
                href={customerMenuUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  borderRadius: 1.5,
                  py: 0.75,
                  px: 1.25,
                  fontSize: 13,
                  color: '#FFFFFF',
                  backgroundColor: 'rgba(255,255,255,0.06)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.10)' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 28, color: 'inherit' }}>
                  <OpenInNew size={16} />
                </ListItemIcon>
                <ListItemText
                  primary={t('sidebar.viewRestaurantPage', 'View Restaurant Page')}
                  primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
                />
              </ListItemButton>
            )}
          </Box>
        )}

        {/* Need help card */}
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: 'rgba(255,255,255,0.04)',
            border: '1px solid',
            borderColor: theme.palette.mesoon?.sidebar?.border || 'rgba(255,255,255,0.08)',
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#FFFFFF', mb: 0.5 }}>
            {t('sidebar.needHelp', 'Need Help?')}
          </Typography>
          <Typography sx={{ fontSize: 12, color: theme.palette.mesoon?.sidebar?.textMuted || '#9DBDB1', mb: 1.5, lineHeight: 1.5 }}>
            {t('sidebar.supportTeam', 'Our support team is here to help you 24/7')}
          </Typography>
          <ListItemButton
            sx={{
              borderRadius: 1.5,
              py: 0.75,
              px: 1.25,
              color: '#FFFFFF',
              backgroundColor: 'rgba(255,255,255,0.06)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.10)' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 28, color: 'inherit' }}>
              <HelpOutline size={16} />
            </ListItemIcon>
            <ListItemText
              primary={t('sidebar.contactSupport', 'Contact Support')}
              primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
            />
          </ListItemButton>
        </Box>
      </Box>
    </Box>
  );

  // Permanent rail on md+, temporary drawer below
  if (variant === 'permanent') {
    return (
      <Drawer
        variant="permanent"
        open
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            border: 'none',
            backgroundColor: theme.palette.mesoon?.sidebar?.bg || '#0F2D24',
          },
        }}
      >
        {railContent}
      </Drawer>
    );
  }

  // Temporary (mobile) — MUI flips `anchor="left"` to right under RTL automatically
  return (
    <Drawer
      variant="temporary"
      anchor="left"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        display: { xs: 'block', md: 'none' },
        '& .MuiDrawer-paper': {
          width: SIDEBAR_WIDTH,
          border: 'none',
        },
      }}
    >
      {railContent}
    </Drawer>
  );
};

export default Sidebar;
